import { Grid, Stack, Typography } from '@mui/material'
import { VesselType } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { BerthPlanningViewStore } from '@planning/pages/BerthPlanning/Stores/BerthPlanningViewStore'
import { getVesselTypeLabel } from '@planning/pages/Vessel/components/Molecules/VesselForm'
import { useTranslate } from '@tolgee/react'
import { DataControlCard, DataControlCardProps, RequiredField, useMinimalsTheme } from '@tom-ui/ui'
import _ from 'lodash'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { VesselVisitGeneralInformationFormStore } from '../../../Stores/VesselVisitGeneralInformationFormStore'
import { VesselVisitViewAttribute } from '../../molecules/VesselVisitViewAttribute'
import { VesselVisitGeneralInformationSubForm } from './VesselVisitGeneralInformationSubForm'

export type VesselVisitGeneralInformationViewProps = {
  vesselNames: string
  vesselType?: VesselType
  vesselOperator?: string | null
  shippingAgent?: string | null
  vesselService?: string
  unCode?: string
  voyageNumber?: string
  visitNumber?: string | null
  portCallId?: string
}

const VesselVisitGeneralInformationViewMode = observer(
  (props: VesselVisitGeneralInformationViewProps) => {
    const {
      vesselNames,
      vesselOperator,
      shippingAgent,
      vesselService,
      unCode,
      voyageNumber,
      visitNumber,
      portCallId,
      vesselType,
    } = props

    const { t } = useTranslate()
    const theme = useMinimalsTheme()

    const hasData = !!(
      vesselNames?.length ||
      vesselOperator ||
      shippingAgent ||
      vesselService ||
      unCode ||
      voyageNumber ||
      visitNumber ||
      portCallId ||
      vesselType
    )

    return hasData ? (
      <Stack direction='column'>
        {visitNumber && (
          <VesselVisitViewAttribute
            label={t('vesselVisitNo', 'Vessel visit no')}
            value={visitNumber ?? ''}
          />
        )}

        {vesselNames && (
          <VesselVisitViewAttribute label={t('vesselName', 'Vessel name')} value={vesselNames} />
        )}

        {vesselType && (
          <VesselVisitViewAttribute
            label={t('vesselType', 'Vessel type')}
            value={getVesselTypeLabel(vesselType, t)}
          />
        )}

        {vesselOperator && (
          <VesselVisitViewAttribute
            label={t('vesselOperator', 'Vessel operator')}
            value={vesselOperator}
          />
        )}

        {shippingAgent && (
          <VesselVisitViewAttribute
            label={t('shippingAgent', 'Shipping agent')}
            value={shippingAgent}
          />
        )}

        {portCallId && (
          <VesselVisitViewAttribute label={t('portCallId', 'Port call ID')} value={portCallId} />
        )}

        {vesselService && (
          <VesselVisitViewAttribute
            label={t('vesselService', 'Vessel service')}
            value={vesselService}
          />
        )}

        {unCode && <VesselVisitViewAttribute label={t('unCode', 'UN Code')} value={unCode} />}

        {voyageNumber && (
          <VesselVisitViewAttribute label={t('voyageNo', 'Voyage no.')} value={voyageNumber} />
        )}
      </Stack>
    ) : (
      <Typography variant='body2' color={theme.palette.text.secondary}>
        {t('noInformationYet', 'No information yet')}
      </Typography>
    )
  },
)

type VesselVisitGeneralInformationEditModeProps = {
  store: VesselVisitGeneralInformationFormStore
  berthPlanningViewStore: BerthPlanningViewStore
  isEditMode: boolean
}

const VesselVisitGeneralInformationEditMode = observer(
  (props: VesselVisitGeneralInformationEditModeProps) => {
    return (
      <Grid container spacing={2} width={'100%'}>
        <Grid size={{ xs: 12 }}>
          <VesselVisitGeneralInformationSubForm {...props} />
        </Grid>
      </Grid>
    )
  },
)

type VesselVisitGeneralInformationControlCardProps = Partial<DataControlCardProps> & {
  vesselVisitGeneralInformationFormStore: VesselVisitGeneralInformationFormStore
  berthPlanningViewStore: BerthPlanningViewStore
  isEditMode: boolean
}

export const VesselVisitGeneralInformationControlCard = observer(
  (props: VesselVisitGeneralInformationControlCardProps) => {
    const { vesselVisitGeneralInformationFormStore, berthPlanningViewStore, isEditMode } = props
    const { vesselServiceItemStore } = usePlanningStore()
    const { t } = useTranslate()

    const { data, selectedVesselNames } = vesselVisitGeneralInformationFormStore
    const {
      vesselOperator,
      shippingAgent,
      vesselServiceId,
      portRotation,
      voyages,
      visitNumber,
      portCallId,
      vessel,
    } = data

    const vesselServiceLabel = computed(
      () =>
        _.values(vesselServiceItemStore.elements).find(
          vesselService => vesselService.data.id === vesselServiceId,
        )?.data.name,
    ).get()

    const voyageLabel = computed(() =>
      voyages
        ?.filter(voyage => voyage.inboundNumber && voyage.outboundNumber)
        ?.map(voyage => {
          const vesselOperator = voyage.shippingLine?.name ? ` - ${voyage.shippingLine.name}` : ''
          return `${voyage.inboundNumber} / ${voyage.outboundNumber}${vesselOperator}`
        })
        .join('\n'),
    ).get()

    return (
      <DataControlCard
        {...props}
        allowEdit
        title={
          <>
            {t('vessel', 'Vessel')} <RequiredField />
          </>
        }
        autoFocusId='vesselIds'
        dataCy='vessel-general-info'
        viewComponent={
          <VesselVisitGeneralInformationViewMode
            vesselNames={selectedVesselNames}
            vesselOperator={vesselOperator?.name}
            shippingAgent={shippingAgent?.name}
            vesselService={vesselServiceLabel}
            unCode={portRotation?.join(', ')}
            portCallId={portCallId ?? ''}
            voyageNumber={voyageLabel}
            visitNumber={visitNumber}
            vesselType={vessel?.vesselType}
          />
        }
        editComponent={
          vesselVisitGeneralInformationFormStore && (
            <VesselVisitGeneralInformationEditMode
              store={vesselVisitGeneralInformationFormStore}
              berthPlanningViewStore={berthPlanningViewStore}
              isEditMode={isEditMode}
            />
          )
        }
        // isEditMode here referes to the edit mode of the page, not the card
        defaultEditMode={!isEditMode}
      />
    )
  },
)
